import { useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { useMe } from '@/hooks/use-me';
import type { TimeseriesMetric } from '@/pcs/domain/entities/pcs-timeseries';
import { IPCSRepository } from '@/pcs/domain/repositories/pcs.repository';
import { PCSOverviewContext } from '@/pcs/view/pages/pcs-overview';
import { GetPowerCapability } from '@/pcs/view/queries/get-power-capability';
import { formatPower, powerLabels } from '@/utils/format/battery';

import { PlotSkeleton } from '../../skeletons/PlotSkeleton';
import ChartInfoBox from '../chart-info-box';
import { getBaseChartOptions } from '../common-chart-config';
import { Placeholder } from '../placeholder';
import { PlotSection } from '../plot-section';

import { generatePowerCapabilitySeries } from './power-capability-series';

export type GetPowerCapabilityPlotProps = {
  filters: IPCSRepository.Filters;
};

export function GetPowerCapabilityPlot({
  filters,
}: GetPowerCapabilityPlotProps) {
  const asset = useContext(PCSOverviewContext);

  // NOTE: assertion for asset here, as we assume the asset is always available
  const powerCapabilityResult = GetPowerCapability.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
  });

  const me = useMe();

  if (powerCapabilityResult.isLoading) {
    return <PlotSkeleton sectionName="PCS Efficiency" />;
  }
  if (powerCapabilityResult.isError) {
    return <div>Error loading data</div>;
  }

  const dataSection: TimeseriesMetric = powerCapabilityResult.data;

  // check if any data, if not show placeholder instead of chart
  const hasData = dataSection.lines.some((line) =>
    line.subLines.some((sub) =>
      sub.data.some(
        (point) => point.value !== null && point.value !== undefined,
      ),
    ),
  );

  const { series, plotLines } = generatePowerCapabilitySeries(dataSection);

  const unit = me.config.powerUnit;
  const chartTitle = `${dataSection.metricName} Over Time`;

  const baseConfig = getBaseChartOptions({
    title: chartTitle,
    xAxisTitle: 'Time',
    yAxisTitle: `${dataSection.metricName} (${powerLabels[unit]})`,
    yAxisMax: null, // Maximum value remains unbounded
    yAxisMin: null, // No limits for this chart
    // Not reversed for capability chart
    plotLines, // custom from the series generator
  });

  const chartOptions: Highcharts.Options = {
    ...baseConfig,
    yAxis: {
      ...baseConfig.yAxis,
      labels: {
        formatter() {
          return formatPower(this.value as number, unit);
        },
      },
    },
    tooltip: {
      pointFormatter() {
        return `${this.series.name}: <strong>${formatPower(
          this.y as number,
          me.config.powerUnit,
        )}</strong>`;
      },
    },
    series,
  };

  const inactive = !hasData;

  return (
    <PlotSection
      sectionName={dataSection.metricName}
      sectionStatus={hasData ? dataSection.metricStatus : undefined}
      inactive={inactive}
    >
      <div className="flex flex-row gap-x-10">
        <div className="min-h-[400px] flex-1 sm:w-3/4">
          {hasData ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              className="w-full"
            />
          ) : (
            <Placeholder
              title="Power Capability data unavailable"
              message="Suitable SOC data required to unlock this chart and gain insights into your PCS's power capability."
            />
          )}
        </div>

        <ChartInfoBox
          description={`The Power Capability refers to the ability of the BESS to deliver a certain
            power during charge and discharge.
            <br/>
            <br/>
            It is directly influenced by the number of connected subsystems behind the PCS and
            their SOC. Subsystems at very high SOCs cannot be charged further and thus do not
            contribute to the charge capability, while subsystems at very low SOCs cannot be
            discharged further.
            <br/>
            <br/>
            The power capability for charge and discharge is averaged over 10‑min intervals; the
            min, max and average of these intervals are shown for each day.`}
        />
      </div>
    </PlotSection>
  );
}
